import React, { useEffect, useMemo, useRef, useState } from 'react'
import { CornerDownLeft, Search, Zap } from 'lucide-react'
import { cn } from '../../utils/cn'

export interface PaletteEntry {
  id: string
  type: 'page' | 'action'
  label: string
  keywords?: string
  /** Gruppe/Herkunft, rechts angezeigt. */
  kind?: string
  icon?: React.ReactNode
  run: () => void
}

function norm(s: string) {
  return s.toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
}

/** Alle Suchwörter müssen vorkommen; frühe Treffer zählen mehr. */
function score(query: string, text: string) {
  if (!query) return 1
  const t = norm(text)
  let s = 0
  for (const w of query.split(/\s+/).filter(Boolean)) {
    const ix = t.indexOf(w)
    if (ix < 0) return 0
    s += 2 - Math.min(ix, 40) / 40
  }
  return s
}

export interface CommandPaletteProps {
  open: boolean
  onClose: () => void
  pages: PaletteEntry[]
  actions: PaletteEntry[]
  showHints?: boolean
}

/** Befehlspalette (Strg+K): Suche über Seiten des Moduls und registrierte Aktionen. */
export function CommandPalette({ open, onClose, pages, actions, showHints = true }: CommandPaletteProps) {
  const [query, setQuery] = useState('')
  const [sel, setSel] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const lastFocus = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (open) {
      lastFocus.current = document.activeElement as HTMLElement | null
      setQuery('')
      setSel(0)
      requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }))
    } else if (lastFocus.current) {
      const el = lastFocus.current
      lastFocus.current = null
      if (document.contains(el)) el.focus({ preventScroll: true })
    }
  }, [open])

  const { flat, pageRes, actRes } = useMemo(() => {
    const q = norm(query.trim())
    const rank = (list: PaletteEntry[]) => {
      const r = list.map(e => ({ e, s: score(q, `${e.label} ${e.keywords ?? ''} ${e.kind ?? ''}`) })).filter(x => x.s > 0)
      if (q) r.sort((a, b) => b.s - a.s)
      return r.map(x => x.e)
    }
    const pageRes = rank(pages)
    const actRes = rank(actions)
    return { flat: [...pageRes, ...actRes], pageRes, actRes }
  }, [query, pages, actions])

  useEffect(() => { setSel(s => Math.min(s, Math.max(flat.length - 1, 0))) }, [flat.length])
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-ix="${sel}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [sel])

  if (!open) return null

  const run = (e?: PaletteEntry) => {
    if (!e) return
    onClose()
    // nach dem Schließen ausführen, damit der Fokus-Rücksprung nicht dazwischenfunkt
    setTimeout(e.run, 0)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (flat.length) setSel((sel + 1) % flat.length) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); if (flat.length) setSel((sel - 1 + flat.length) % flat.length) }
    else if (e.key === 'Enter') { e.preventDefault(); run(flat[sel]) }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose() }
    else if (e.key === 'Tab') {
      // Fokus bleibt in der Palette
      e.preventDefault()
      inputRef.current?.focus()
    }
  }

  let ix = 0
  const row = (e: PaletteEntry) => {
    const i = ix++
    return (
      <div
        key={e.id}
        id={`ui-pal-o${i}`}
        data-ix={i}
        role="option"
        aria-selected={i === sel}
        className="ui-pal-opt"
        onPointerMove={() => i !== sel && setSel(i)}
        onClick={() => run(e)}
      >
        <span className="ui-pal-ic" aria-hidden="true">{e.icon ?? <Zap />}</span>
        <span className="min-w-0 flex-1 truncate">{e.label}</span>
        {e.kind && <span className="ui-pal-kind">{e.kind}</span>}
      </div>
    )
  }

  return (
    <div className="ui-pal" role="dialog" aria-modal="true" aria-label="Befehlspalette" onPointerDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="ui-pal-box ui-pop" ref={boxRef} onKeyDown={onKeyDown}>
        <div className="ui-pal-in">
          <Search className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setSel(0) }}
            type="text"
            placeholder="Seite oder Aktion suchen…"
            role="combobox"
            aria-expanded="true"
            aria-controls="ui-pal-list"
            aria-activedescendant={flat.length ? `ui-pal-o${sel}` : undefined}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="ui-pal-list" id="ui-pal-list" role="listbox" ref={listRef}>
          {pageRes.length > 0 && <div className="ui-pal-grp">Seiten</div>}
          {pageRes.map(row)}
          {actRes.length > 0 && <div className="ui-pal-grp">Aktionen</div>}
          {actRes.map(row)}
          {flat.length === 0 && <div className="ui-pal-empty">Keine Treffer für „{query}“</div>}
        </div>
        {showHints && (
          <div className={cn('ui-pal-foot')}>
            <span>↑↓ wählen</span>
            <span className="inline-flex items-center gap-1"><CornerDownLeft className="h-3 w-3" aria-hidden="true" /> ausführen</span>
            <span>Esc schließen</span>
          </div>
        )}
      </div>
    </div>
  )
}
